import React, { useRef } from 'react';
import {
  useHamburgerMenuState,
  useStickyHeader,
  useCityPickerState,
  useLoyaltyAccount,
} from 'components/header/hooks';
import { HeaderModelData } from 'types/model';
import { useIsCheckout } from 'components/header/hooks/use-is-checkout';
import { MenuMobile } from '../menu-mobile';
import { headerFullScreenClassName, isVisible } from './styles';
import { HeaderContent } from './header-content';
import { withContextSetup, withModel } from '../hocs';
import { MainMenu } from '../main-menu';
import { RegionsMenu } from '../regions-menu';
import { State } from 'store/types';

import { useStoreon } from 'storeon/react';
import { isEnableLoyaltyRegistrationBanner, isUserAuth } from 'selectors';
import { LoyaltyRegistrationModal } from 'components/registration/registration';
import { useAutoShowLoyaltyRegistrationModal } from '../../hooks/use-auto-show-loyalty-registration-modal';
import { DeviceType } from 'types';
import { StickyContainer } from './sticky-container';
import { ExternalDependencies } from '../external-dependencies/external-dependencies';

type Props = {
  model?: HeaderModelData;
  disableAppsflyer?: boolean;
  disableSticky?: boolean;
  withoutRegionModalButton?: boolean;
};

export const HeaderLayout = ({
  model = {} as HeaderModelData,
  disableAppsflyer,
  disableSticky,
  withoutRegionModalButton,
}: Props) => {
  const { cookies, isBot, deviceType } = useStoreon<State>('cookies', 'isBot', 'deviceType');
  const isAuth = isUserAuth(cookies);
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const headerContainerHeight = headerContainerRef?.current?.clientHeight || 0;
  const isStickyVisible = useStickyHeader(headerRef, isVisible, !!disableSticky, 10, headerContainerHeight);
  const enableLoyaltyRegistrationBanner = isEnableLoyaltyRegistrationBanner(model?.experiments);

  useLoyaltyAccount({
    enabled: isAuth && enableLoyaltyRegistrationBanner,
  });
  useAutoShowLoyaltyRegistrationModal();

  const hamburgerMenuState = useHamburgerMenuState();
  const regionsMenuState = useCityPickerState();

  return (
    <>
      <ExternalDependencies disableAppsflyerByConsumer={disableAppsflyer} />
      <div ref={headerContainerRef}>
        <div className={headerFullScreenClassName}>
          <HeaderContent
            model={model}
            isBot={isBot}
            isStickyVisible={isStickyVisible}
            deviceType={deviceType}
            withoutRegionModalButton={withoutRegionModalButton}
          />
        </div>
        <StickyContainer isBot={isBot} disableSticky={!!disableSticky} ref={headerRef}>
          <HeaderContent
            model={model}
            isBot={isBot}
            isSticky
            isStickyVisible={isStickyVisible}
            deviceType={deviceType}
            withoutRegionModalButton={withoutRegionModalButton}
          />
        </StickyContainer>
        <MobilePartHeader model={model} deviceType={deviceType} />
      </div>
      <MainMenu model={model} {...hamburgerMenuState} />
      <RegionsMenu {...regionsMenuState} model={model} />
      <LoyaltyRegistrationModal />
    </>
  );
};

type MobilePartHeaderProps = {
  model: HeaderModelData;
  deviceType: DeviceType;
};

const MobilePartHeader = ({ model, deviceType }: MobilePartHeaderProps) => {
  const isCheckout = useIsCheckout();

  if (deviceType === 'desktop' || isCheckout) {
    return null;
  }

  return <MenuMobile model={model} />;
};

export const HeaderComponent = withContextSetup(withModel(HeaderLayout));
